// src/ecrans/reglages/EcranReglages.jsx — RÉGLAGES (maquette v677, relevés 266
// à 270 ; décision 56 : quatre onglets, Société · Équipe · Documents · Connexions).
//
// Lu SEULEMENT par `ecran_reglages_lire` (liste blanche) et
// `journey_composition_get` (l'espace voyageur a déjà sa porte). L'écran
// n'écrit rien : chaque changement passe par l'ancien écran, toujours atteint
// (« Aucun geste retiré à l'admin »). Banque, taxe et factures = argent
// (couloir A) ; Équipe = comptes et droits (couloir F) : leur lien seulement.

import { useEffect, useRef, useState } from "react";
import { Building2, FileText, Link2, Users } from "lucide-react";
import { useLecturePorte } from "../portes-g3";
import { StyleG3, EtatG3, LienAncienG3, COULEURS as C } from "../nest-g3";
import OngletConnexions from "./OngletConnexions";
import { blocsSociete, bilanBlocs } from "./reglages-calculs";

const CSS = `
.gr-onglets{display:flex;background:#fff;border-bottom:1px solid ${C.bord};overflow-x:auto}
.gr-onglets button{all:unset;cursor:pointer;flex:1;text-align:center;padding:0 15px;min-height:46px;font-size:12.5px;color:${C.gris};white-space:nowrap;border-bottom:2px solid transparent}
.gr-onglets button.on{color:${C.encre};font-weight:600;border-bottom-color:${C.vert}}
.gr-tete{display:flex;align-items:flex-start;gap:12px}
.gr-tete>div{flex:1;min-width:0}
.gr-manque{all:unset;box-sizing:border-box;cursor:pointer;display:flex;align-items:center;gap:10px;width:100%;background:${C.aubP};border:1px solid ${C.aubBord};color:${C.aubTx};border-radius:12px;padding:10px 12px;margin-bottom:10px;font-size:12.5px;flex-wrap:wrap}
.gr-manque b{flex:1;min-width:170px}
.gr-manque span{background:${C.aub};color:#fff;border-radius:9px;padding:7px 11px;font-size:12px;font-weight:600}
.g3-ligne.gr-ok{background:${C.vertP};border-color:#CFE3D9}
.g3-ligne.gr-ko{background:${C.aubP};border-color:${C.aubBord}}
.gr-bilan{display:flex;gap:14px;align-items:center;background:#fff;border-radius:16px;padding:14px;margin-bottom:12px;border:1px solid ${C.bord}}
.gr-anneau{width:56px;height:56px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:${C.encre};flex-shrink:0;position:relative}
.gr-anneau::after{content:"";position:absolute;inset:6px;border-radius:50%;background:#fff}
.gr-anneau span{position:relative;z-index:1}
.gr-bilan .l{font-size:10px;letter-spacing:.06em;text-transform:uppercase;font-weight:600;color:${C.gris};margin:0}
.gr-bilan .f{font-size:14.5px;font-weight:600;margin:4px 0 0}
.gr-bilan .r{font-size:11px;color:${C.gris};margin:3px 0 0}
.gr-pastilles{display:flex;gap:6px;flex-wrap:wrap;margin-top:7px}
.gr-pastilles button{all:unset;cursor:pointer;font-size:11.5px;font-weight:600;border-radius:9px;padding:5px 10px;background:${C.aubP};color:${C.aubTx}}
.gr-ic{width:32px;height:32px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0;background:#F2F4F3;color:${C.gris}}
.gr-ic.ok{background:${C.vert};color:#fff}
.gr-ic.ko{background:${C.aub};color:#fff}
.gr-corps{background:#fff;border:1px solid ${C.bord};border-radius:12px;padding:10px 12px;margin:-2px 0 8px;font-size:12px}
.gr-ch{display:flex;justify-content:space-between;gap:12px;padding:5px 0;border-bottom:1px solid #F0F2F1}
.gr-ch span{color:${C.gris}}
.gr-ch b{font-weight:600;text-align:right;overflow-wrap:anywhere;min-width:0}
`;

const ONGLETS = [["societe", "Société", Building2], ["equipe", "Équipe", Users], ["documents", "Documents", FileText], ["connexions", "Connexions", Link2]];

function pluriel(n, mot) {
  return `${n} ${mot}${n > 1 ? "s" : ""}`;
}

function Bloc({ b, onAncien, ouvert, setOuvert, cible }) {
  const ligne = useRef(null);
  // Une pastille du bilan ouvre un bloc parfois loin dessous : on l'amène à l'écran.
  useEffect(() => {
    if (ouvert && cible && ligne.current?.scrollIntoView) ligne.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [ouvert, cible]);
  return (
    <>
      <button ref={ligne} type="button" className={`g3-ligne${b.ok === true ? " gr-ok" : b.ok === false ? " gr-ko" : ""}`} aria-expanded={ouvert} data-testid={`bloc-${b.id}`} onClick={() => setOuvert(ouvert ? null : b.id)}>
        <span className={`gr-ic ${b.ok === true ? "ok" : b.ok === false ? "ko" : ""}`} aria-hidden="true">
          {b.ic}
        </span>
        <span className="co">
          <span className="t">{b.t}</span>
          <span className="s" style={b.ok === false ? { color: C.aubTx } : undefined}>
            {b.apercu}
          </span>
        </span>
        <span className="ch">{ouvert ? "⌄" : "›"}</span>
      </button>
      {ouvert ? (
        <div className="gr-corps" data-testid={`bloc-${b.id}-corps`}>
          {(b.champs || []).map(([l, v]) => (
            <div className="gr-ch" key={l}>
              <span>{l}</span>
              <b>{v || "—"}</b>
            </div>
          ))}
          {b.note ? <p className="g3-manque">{b.note}</p> : null}
          <p style={{ margin: "8px 0 0" }}>
            <LienAncienG3 pour={b.geste} onClick={() => onAncien(b.ancien)} testId={`bloc-${b.id}-ancien`} />
          </p>
        </div>
      ) : null}
    </>
  );
}

function OngletSociete({ reglages, espace, manques, onAncien }) {
  const blocs = blocsSociete(reglages, espace);
  const bilan = bilanBlocs(blocs);
  const [ouvert, setOuvert] = useState(null);
  const [cible, setCible] = useState(null);
  return (
    <>
      {manques ? (
        <button type="button" className="gr-manque" data-testid="reglages-manque" onClick={() => onAncien("completude")}>
          <b>
            {pluriel(manques.bloquants, "réglage")} bloquant{manques.bloquants > 1 ? "s" : ""} · {manques.silencieux} silencieux
          </b>
          <span>Voir ce qui manque ›</span>
        </button>
      ) : null}
      <div className="gr-bilan" data-testid="reglages-bilan">
        <div className="gr-anneau" style={{ background: `conic-gradient(${C.aub} ${(bilan.faits / blocs.length) * 360}deg, #EEF1EF 0)` }}>
          <span>
            {bilan.faits}/{blocs.length}
          </span>
        </div>
        <div>
          <p className="l">Ce qui est réglé</p>
          <p className="f">{bilan.manquants.length ? `${pluriel(bilan.manquants.length, "bloc")} à compléter` : "Rien à compléter parmi ce qui se lit"}</p>
          {bilan.manquants.length ? (
            <div className="gr-pastilles" data-testid="reglages-pastilles">
              {blocs
                .filter((b) => b.ok === false)
                .map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => {
                      setCible(b.id);
                      setOuvert(b.id);
                    }}
                  >
                    {b.t} ›
                  </button>
                ))}
            </div>
          ) : null}
          {bilan.inconnus ? <p className="r">{pluriel(bilan.inconnus, "bloc")} sans lecture ici : « — »</p> : null}
        </div>
      </div>
      {blocs.map((b) => (
        <Bloc key={b.id} b={b} onAncien={onAncien} ouvert={ouvert === b.id} cible={cible === b.id} setOuvert={(id) => { setCible(null); setOuvert(id); }} />
      ))}
    </>
  );
}

function OngletDocuments({ documents, onAncien }) {
  const [vue, setVue] = useState("voyageur");
  const c = documents.contrat_voyageur;
  return (
    <>
      <div className="g3-seg" role="tablist" style={{ marginBottom: 12 }}>
        {[["voyageur", "Contrat voyageur"], ["proprietaires", "Contrats propriétaires"]].map(([id, t]) => (
          <button key={id} type="button" role="tab" aria-selected={vue === id} className={vue === id ? "on" : ""} onClick={() => setVue(id)}>
            {t}
          </button>
        ))}
      </div>
      {vue === "voyageur" ? (
        <div className="g3-carte" data-testid="doc-voyageur">
          <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>Le contrat voyageur</p>
          <p className="g3-etat">
            {c.personnalises
              ? `${pluriel(c.personnalises, "logement")} sur ${c.logements} avec ses propres clauses ; les autres suivent les clauses générales.`
              : `Les ${c.logements} logements suivent les clauses générales.`}
          </p>
          <LienAncienG3 pour="modifier le contrat" onClick={() => onAncien("contrat")} testId="doc-voyageur-ancien" />
        </div>
      ) : (
        <div className="g3-carte" data-testid="doc-proprietaires">
          <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>Contrats des propriétaires</p>
          <p className="g3-manque">— L'appli ne garde pas encore ces contrats : aucun compte, aucune clause lue.</p>
        </div>
      )}
    </>
  );
}

export default function EcranReglages({ manques, onAncien, onAccueil }) {
  const [onglet, setOnglet] = useState("societe");
  const [lecture, relire] = useLecturePorte("ecran_reglages_lire");
  const [espace] = useLecturePorte("journey_composition_get", SANS_OK);
  const data = lecture.etat === "ok" ? lecture.data : null;
  return (
    <div className="g3" data-testid="ecran-reglages">
      <StyleG3 />
      <style>{CSS}</style>
      <div className="g3-tete">
        <div className="gr-tete">
          <div>
            <p className="g3-sous">Ce qui se règle une fois</p>
            <h2 className="g3-titre">Réglages</h2>
          </div>
          <button type="button" className="g3-bt" data-testid="reglages-accueil" onClick={onAccueil}>
            ‹ Accueil
          </button>
        </div>
      </div>
      <div className="gr-onglets" role="tablist">
        {ONGLETS.map(([id, t, Icone]) => (
          <button key={id} type="button" role="tab" aria-selected={onglet === id} className={onglet === id ? "on" : ""} onClick={() => setOnglet(id)}>
            <Icone size={15} strokeWidth={2} aria-hidden="true" style={{ verticalAlign: "-3px", marginRight: 7 }} />
            {t}
          </button>
        ))}
      </div>
      <div className="g3-page">
        <EtatG3 lecture={lecture} quoi="Les réglages" onRelire={relire} />
        {onglet === "equipe" ? (
          <div className="g3-carte" data-testid="reglages-equipe">
            <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>Les comptes et leurs accès</p>
            <p className="g3-etat">Les comptes, les rôles et les invitations restent sur l'écran actuel.</p>
            <LienAncienG3 pour="gérer les comptes" onClick={() => onAncien("comptes")} testId="reglages-equipe-ancien" />
          </div>
        ) : null}
        {data && onglet === "societe" ? <OngletSociete reglages={data} espace={espace} manques={manques} onAncien={onAncien} /> : null}
        {data && onglet === "documents" ? <OngletDocuments documents={data.documents} onAncien={onAncien} /> : null}
        {data && onglet === "connexions" ? <OngletConnexions connexions={data.connexions} onAncien={onAncien} /> : null}
      </div>
    </div>
  );
}

const SANS_OK = { sansOk: true };
